// EstimateVariantTabs — side-by-side "Option A / Option B / ..." switcher for
// the Estimate page. Each variant is a full priced estimate for the same
// opportunity; the "primary" variant is what pipeline forecast / PDF export
// use as the headline number.  Operators bid multiple variants when a
// government solicitation allows alt scopes (e.g. full replacement vs. repair).
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Plus, Star, X, Pencil, Check } from "lucide-react";

const fmtMoney = (n) => (typeof n === "number" && !isNaN(n) ? `$${Math.round(n).toLocaleString()}` : "—");

export const EstimateVariantTabs = ({ noticeId, currentEstimate, onVariantSwitch }) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/ai/estimate/${noticeId}/variants`);
      setVariants(r.data?.variants || []);
    } catch (e) {
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (noticeId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, currentEstimate?.grand_total]);

  const primaryId = (variants.find((v) => v.is_primary) || variants[0])?.variant_id;

  const switchVariant = async (variantId) => {
    if (variantId === primaryId) return;
    try {
      await api.post(`/ai/estimate/${noticeId}/variants/${variantId}/select`);
      await load();
      onVariantSwitch?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not switch variant");
    }
  };

  const createVariant = async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const r = await api.post(
        `/ai/estimate/${noticeId}/variants`,
        {},
        { timeout: 240000 }
      );
      // Auto-switch to the newly created variant.
      await api.post(`/ai/estimate/${noticeId}/variants/${r.data.variant_id}/select`);
      await load();
      onVariantSwitch?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create variant");
    } finally {
      setCreating(false);
    }
  };

  const deleteVariant = async (variantId, name) => {
    if (variants.length <= 1) {
      setError("Cannot delete the only variant. Regenerate to replace it.");
      return;
    }
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/ai/estimate/${noticeId}/variants/${variantId}`);
      await load();
      onVariantSwitch?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete variant");
    }
  };

  const commitRename = async (variantId) => {
    const name = renameValue.trim();
    if (!name) {
      setRenamingId(null);
      return;
    }
    try {
      await api.patch(`/ai/estimate/${noticeId}/variants/${variantId}`, { name });
      setRenamingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not rename");
    }
  };

  // Hide the whole strip until we have >1 variant OR the user has generated an
  // estimate at all — otherwise a lonely single-tab bar is just noise.
  if (loading) return null;
  if (variants.length === 0 && !currentEstimate) return null;

  return (
    <div className="mb-6" data-testid="estimate-variant-tabs">
      <div className="flex flex-wrap items-stretch gap-2">
        {variants.map((v) => {
          const isActive = v.variant_id === primaryId;
          const isRenaming = renamingId === v.variant_id;
          return (
            <div
              key={v.variant_id}
              className={`group border rounded-md pl-3 pr-2 py-2 flex items-center gap-3 cursor-pointer transition-colors ${
                isActive
                  ? "border-fuchsia-400 bg-fuchsia-400/10"
                  : "border-white/10 bg-white/[0.02] hover:border-white/30"
              }`}
              onClick={() => !isRenaming && switchVariant(v.variant_id)}
              data-testid={`variant-tab-${v.variant_id}`}
            >
              {v.is_primary && (
                <Star size={13} className="text-amber-300 fill-amber-300 flex-shrink-0" />
              )}
              {isRenaming ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(v.variant_id);
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  onBlur={() => commitRename(v.variant_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-b border-fuchsia-400 outline-none text-sm text-white w-32"
                  data-testid={`variant-rename-input-${v.variant_id}`}
                />
              ) : (
                <div className="min-w-0">
                  <div className={`text-sm font-medium truncate ${isActive ? "text-white" : "text-slate-300"}`}>
                    {v.name}
                  </div>
                  <div className={`text-base font-semibold tabular-nums ${isActive ? "text-fuchsia-100" : "text-slate-200"}`} data-testid={`variant-total-${v.variant_id}`}>
                    {fmtMoney(v.grand_total)}
                  </div>
                </div>
              )}
              {!isRenaming && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameValue(v.name);
                      setRenamingId(v.variant_id);
                    }}
                    className="p-1 hover:bg-white/10 rounded"
                    title="Rename"
                    data-testid={`variant-rename-btn-${v.variant_id}`}
                  >
                    <Pencil size={11} className="text-slate-400" />
                  </button>
                  {variants.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteVariant(v.variant_id, v.name);
                      }}
                      className="p-1 hover:bg-red-500/20 rounded"
                      title="Delete"
                      data-testid={`variant-delete-btn-${v.variant_id}`}
                    >
                      <X size={11} className="text-slate-400 hover:text-red-300" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button
          onClick={createVariant}
          disabled={creating || variants.length >= 6}
          className="border border-dashed border-white/20 hover:border-fuchsia-400 hover:bg-fuchsia-400/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm text-slate-400 hover:text-fuchsia-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="variant-add-btn"
          title={variants.length >= 6 ? "Max 6 variants per opportunity" : "Add alternate scope variant"}
        >
          {creating ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              <span>Generating alternate scope…</span>
            </>
          ) : (
            <>
              <Plus size={14} />
              <span>New Variant</span>
            </>
          )}
        </button>
      </div>
      {error && (
        <div className="mt-2 text-xs text-red-300" data-testid="variant-error">{error}</div>
      )}
      {variants.length > 1 && (
        <div className="mt-2 text-[11px] text-slate-500">
          <Star size={10} className="inline text-amber-300 fill-amber-300 mr-1" />
          <span>Primary variant — used for pipeline forecast and top-of-bid PDF. Click any tab to switch.</span>
        </div>
      )}
    </div>
  );
};

export default EstimateVariantTabs;
